
<template>
  <div>
    <!-- Mobile View -->
    <MobileHomeView v-if="isMobile" />
    
    <!-- Desktop View -->
    <div v-else>
    <!-- Banner with custom load animation -->
    <transition name="custom-banner" appear>
      <div id="home" class="relative h-screen flex flex-col justify-center">
        <GlobalHeader
          mode="overlay"
          :cta-to="{ path: '/products' }"
          :use-aos="true"
        >
          <template #actions>
            <button
              type="button"
              class="global-header__cart-btn"
              @click="toggleCart"
            >
              <span class="global-header__cart-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                  <path d="M7 6h-2l-1 2v1h2l3.6 7.59-1.35 2.41c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h10v-2h-9.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.6h6.74c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.09-.16.14-.34.14-.53a1 1 0 0 0-1-1h-15.31l-.94-2zm0 0" />
                  <circle cx="10" cy="21" r="1" />
                  <circle cx="18" cy="21" r="1" />
                </svg>
              </span>
              Cart ({{ cartCount }})
            </button>
          </template>
        </GlobalHeader>
        <!-- Background image -->
        <div class="absolute inset-0 z-0">
          <img
            class="w-full h-full object-cover"
            src="https://firebasestorage.googleapis.com/v0/b/bio-edge.firebasestorage.app/o/ChatGPT%20Image%20Oct%2023%2C%202025%2C%2009_02_25%20PM.png?alt=media&token=14e92603-4f6f-45fe-bf05-09f51c7a8ddd" alt="Background"
          />
        </div>

        <!-- Main banner content -->
        <div class="relative z-20 banner-shell">
          <div class="banner-content w-full lg:w-2/3 xl:w-1/2">
            <span class="banner-eyebrow">Transform Your Life</span>
            <h1 class="banner-title">
              <span>Shape</span>
              <span>Your Body</span>
            </h1>

            <p class="banner-subtext">
              Begin your weight loss journey with BioEdge. Our premium supplements
              and personalized support are designed to help you achieve lasting results.
            </p>

            <div class="banner-actions flex items-center mt-8 space-x-3 sm:space-x-4">
              <RouterLink to="/products" title="Shop BioEdge" class="hero-btn banner-primary-btn">
                Shop Now
              </RouterLink>

              <a
                href="#contact"
                title="Contact Us"
                class="hero-btn banner-secondary-btn"
                role="button"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- TEMPORARY: Firebase Test Buttons -->

    <transition name="cart-slide">
      <CartDrawer
        v-if="cartVisible"
        :items="cartItems"
        :total="cartTotal"
        :format-price="formatPrice"
        :get-item-price="getCartItemPrice"
        @close="toggleCart"
        @increase="increaseCartItem"
        @decrease="decreaseCartItem"
        @remove="removeFromCart"
        @checkout="checkoutCart"
      >
        <template #empty>
          Your cart is currently empty.
        </template>
      </CartDrawer>
    </transition>


    <!-- Our Services Section -->
    <section
      id="services"
      class="py-10 bg-white sm:py-16 lg:py-24"
      data-aos="fade-up"
      data-aos-duration="1000"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="max-w-3xl mx-auto text-center">
          <header class="py-6">
            <span class="services-eyebrow">Our Services</span>
            <h2 class="services-heading">
              Fitness improves physical health. Regular workouts strengthen the heart and improve metabolism
            </h2>
          </header>
        </div>

        <div class="services-grid">
          <div class="service-item">
            <div class="service-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="w-12 h-12">
                <path d="M14 10h-4v4h4z" />
                <path d="M21 10h-3v4h3zM6 10H3v4h3z" />
                <path d="M17 10h-3v4h3zM10 10H7v4h3z" />
              </svg>
            </div>
            <h3 class="service-title">GLP&#8209;1 Support</h3>
            <p class="service-desc">Evidence&#8209;based support to complement GLP&#8209;1 therapy and accelerate safe, sustainable fat loss.</p>
          </div>
          <div class="service-item">
            <div class="service-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="w-12 h-12">
                <path d="M7 7h10M6 12c2 0 3-3 3-5m9 5c-2 0-3-3-3-5" />
                <path d="M8 17c1.5-1 2-2 4-2s2.5 1 4 2" />
              </svg>
            </div>
            <h3 class="service-title">Effective Weight Loss</h3>
            <p class="service-desc">Fast, comfortable and safe progress with structured milestones and weekly check&#8209;ins.</p>
          </div>
          <div class="service-item">
            <div class="service-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="w-12 h-12">
                <path d="M7 15c0 3 2 5 5 5s5-2 5-5-2-5-5-5" />
                <path d="M12 10c0-2 2-4 4-4" />
                <path d="M6 12c-1 0-2-1-2-2 0-1 1-2 2-2 1 0 2 1 2 2 0 1-1 2-2 2z" />
              </svg>
            </div>
            <h3 class="service-title">Personalized Nutrition</h3>
            <p class="service-desc">Choosing the optimal nutrition plan for your goals, preferences and biomarkers.</p>
          </div>
          <div class="service-item">
            <div class="service-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="w-12 h-12">
                <path d="M4 15c2 3 6 5 10 3 2-1 3-3 3-5V9" />
                <path d="M13 8c1-1 2-2 4-2 1 0 2 1 2 2" />
              </svg>
            </div>
            <h3 class="service-title">Lifestyle Coaching</h3>
            <p class="service-desc">Habit systems, activity guidance and sleep routines that sustain results long&#8209;term.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- About Us (Refreshed) -->
<section id="about" class="py-8 bg-white" data-aos="fade-up" data-aos-duration="1000">
  <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
      <div class="flex justify-center md:justify-start">
        <img :src="personImg" alt="BioEdge Coach" class="w-full max-w-md object-contain" />
      </div>
      <div>
        <span class="services-eyebrow">About Us</span>
        <h2 class="services-heading" style="font-size: clamp(2rem, 4.5vw, 3.25rem); line-height:1.1;">Maximum comfort and care for your wellness journey</h2>
        <p class="banner-subtext" style="color:#111827; text-shadow:none; max-width:40rem;">We develop a unique plan for weight loss and a positive tone for your whole body — powered by nutrition, GLP-1 support and lifestyle coaching.</p>
        <div class="grid grid-cols-3 gap-4 mt-6">
          <div>
            <div class="text-5xl leading-none font-extrabold" style="color:#52CD36;">165+</div>
            <div class="text-sm font-semibold text-black mt-1">Happy Customers</div>
          </div>
          <div>
            <div class="text-5xl leading-none font-extrabold" style="color:#52CD36;">98%</div>
            <div class="text-sm font-semibold text-black mt-1">Efficiency</div>
          </div>
          <div>
            <div class="text-5xl leading-none font-extrabold" style="color:#52CD36;">12</div>
            <div class="text-sm font-semibold text-black mt-1">Years Experience</div>
          </div>
        </div>
        <div class="mt-8">
          <RouterLink to="/products" class="hero-btn banner-primary-btn">Get Now</RouterLink>
        </div>
      </div>
    </div>
  </div>
</section>
<!-- Our Products Section -->
    <section
      id="products"
      data-aos="fade-up"
      data-aos-duration="1000"
      class="py-10 bg-white sm:py-16 lg:py-24 font-sans"
    >
      <div class="container mx-auto">
        <header class="text-center mb-10" data-aos="fade-up" data-aos-duration="800">
          <span class="services-eyebrow">Our Products</span>
          <h2 class="services-heading">
            Discover our exclusive range of scientifically backed supplements
          </h2>
          <p class="products-subheading">
            Curated formulas designed to support sustainable weight loss, hormonal balance, and lasting energy.
          </p>
        </header>
        <div class="relative">
          <button @click="scrollLeft" class="md:hidden absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white rounded-full shadow">
            &#8592;
          </button>
          <div ref="productSlider" class="home-products-slider overflow-x-auto py-4 hide-scrollbar">
            <div class="home-products-track">
              <ProductCard
                v-for="(product, index) in recentProducts"
                :key="product.firebaseId || product.id || index"
                :product="product"
                :format-price="formatPrice"
                class="home-products-card"
                data-aos="flip-left"
                data-aos-duration="800"
                @add-to-cart="addToCart"
              />
            </div>
          </div>
          <button @click="scrollRight" class="md:hidden absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white rounded-full shadow">
            &#8594;
          </button>
        </div>
        <div class="mt-8 text-center">
          <RouterLink to="/products" class="view-all-link">
            View all products
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- Order Details Modal with Order Items -->
    <OrderDetailsDialog
      v-if="showOrderDialog"
      :order-items="orderItems"
      :order-total="orderTotal"
      :submitting="orderSubmitting"
      :format-price="formatPrice"
      :get-item-price="getCartItemPrice"
      :shipping-options="shippingOptions"
      :shipping-option="shippingOption"
      :shipping-cost="shippingCost"
      :first-name="orderFirstName"
      :last-name="orderLastName"
      :email="orderEmail"
      :cell="orderCell"
      :address="orderAddress"
      @cancel="cancelOrder"
      @submit="submitOrder"
      @update:firstName="orderFirstName = $event"
      @update:lastName="orderLastName = $event"
      @update:email="orderEmail = $event"
      @update:cell="orderCell = $event"
      @update:address="orderAddress = $event"
      @update:shippingOption="shippingOption = $event"
    >
      <template #empty>
        Your cart is empty. Add products to your cart to continue.
      </template>
    </OrderDetailsDialog>


    <!-- Testimonials Section -->
    <section class="testimonials-section" data-aos="fade-up" data-aos-duration="1000">
      <div class="max-w-6xl px-6 mx-auto">
        <header class="text-center testimonials-header" data-aos="fade-up" data-aos-duration="800">
          <span class="services-eyebrow">Testimonials</span>
          <h2 class="services-heading">What our clients are sharing</h2>
          <p class="testimonials-subheading">
            Real stories from people who trusted BioEdge to guide their transformation.
          </p>
        </header>

        <div class="testimonials-grid">
          <article
            class="testimonials-card"
            data-aos="zoom-in"
            data-aos-duration="800"
          >
            <div class="testimonials-card__header">
              <img
                class="testimonials-card__avatar"
                src="https://images.unsplash.com/photo-1499714608240-22fc6ad53fb2?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80"
                alt="client photo"
              />
              <div>
                <p class="testimonials-card__name">John Smith</p>
                <p class="testimonials-card__location">Cape Town</p>
              </div>
            </div>
            <p class="testimonials-card__quote">
              “BioEdge has completely transformed my approach to weight loss. I feel more energized, and the results speak for themselves!”
            </p>
          </article>

          <article
            class="testimonials-card"
            data-aos="zoom-in"
            data-aos-duration="800"
          >
            <div class="testimonials-card__header">
              <img
                class="testimonials-card__avatar"
                src="https://www.famousbirthdays.com/faces/brown-sarah-image.jpg"
                alt="client photo"
              />
              <div>
                <p class="testimonials-card__name">Sarah Brown</p>
                <p class="testimonials-card__location">Johannesburg</p>
              </div>
            </div>
            <p class="testimonials-card__quote">
              “I’ve lost over 12 kg with the help of BioEdge’s products and guidance. Their support system is amazing—I finally feel in control of my health.”
            </p>
          </article>

          <article
            class="testimonials-card"
            data-aos="zoom-in"
            data-aos-duration="800"
          >
            <div class="testimonials-card__header">
              <img
                class="testimonials-card__avatar"
                src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80"
                alt="client photo"
              />
              <div>
                <p class="testimonials-card__name">Aisha Khan</p>
                <p class="testimonials-card__location">Durban</p>
              </div>
            </div>
            <p class="testimonials-card__quote">
              “The GLP-1 support was a game-changer. BioEdge’s team made it so easy to incorporate healthy habits—I’ve never felt more confident.”
            </p>
          </article>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <ContactFooter />

    </div>
  </div>
</template>

<script>
import AOS from 'aos'
import 'aos/dist/aos.css'
import personImg from '../assets/person.png'
import GlobalHeader from '../components/GlobalHeader.vue'
import ContactFooter from '../components/ContactFooter.vue'
import ProductCard from '../components/ProductCard.vue'
import CartDrawer from '../components/CartDrawer.vue'
import OrderDetailsDialog from '../components/OrderDetailsDialog.vue'
import MobileHomeView from './MobileHomeView.vue'
import { processCheckoutOrder } from '../utils/checkoutIntegration'
import { formatOrderedItemsForWhatsApp } from '../utils/whatsappOrderText'
import {
  loadCheckoutContact,
  saveCheckoutContact,
  applyCheckoutContactToData
} from '../utils/checkoutContactStorage'
import { testFirebaseConnection, testCreateClient } from '../utils/testFirebase'
import { productService } from '../services/firebaseService'

export default {
  name: 'BioEdgeComponent',
  components: {
    GlobalHeader,
    ContactFooter,
    ProductCard,
    CartDrawer,
    OrderDetailsDialog,
    MobileHomeView
  },
  data() {
    return {
      personImg,
      products: [],
      showOrderDialog: false,
      orderFirstName: '',
      orderLastName: '',
      orderEmail: '',
      orderCell: '',
      orderAddress: '',
      orderItems: [],
      orderSubmitting: false,
      cartItems: [],
      cartVisible: false,
      shippingOption: 'standard'
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
    shippingOptions() {
      return [
        { value: 'standard', label: 'Standard Delivery', description: '2-3 days', price: 150 },
        { value: 'express', label: 'Express Delivery', description: '1-2 days', price: 250 }
      ];
    },
    selectedShippingOption() {
      return this.shippingOptions.find(option => option.value === this.shippingOption) || this.shippingOptions[0];
    },
    shippingCost() {
      if (!this.orderItems.length) {
        return 0;
      }
      return this.selectedShippingOption?.price || 0;
    },
    orderSubtotal() {
      return this.orderItems.reduce((total, item) => {
        const product = this.products.find(p => p.name === item.product);
        const price = this.getProductPrice(product);
        return total + price * (item.quantity || 0);
      }, 0);
    },
    orderTotal() {
      return this.orderSubtotal + this.shippingCost;
    },
    cartCount() {
      return this.cartItems.reduce((total, item) => total + item.quantity, 0);
    },
    cartTotal() {
      return this.cartItems.reduce(
        (total, item) => total + this.getCartItemPrice(item) * (item.quantity || 0),
        0
      );
    },
    recentProducts() {
      return [...this.products]
        .sort((a, b) => this.getProductTimestamp(b) - this.getProductTimestamp(a))
        .slice(0, 3);
    },
    isMobile() {
      if (typeof window === 'undefined') return false
      return window.innerWidth < 1024
    }
  },
  methods: {
    handleResize() {
      this.$forceUpdate() // Force re-render to update isMobile
    },
    getProductTimestamp(product) {
      const source =
        product?.createdAt ||
        product?.updatedAt ||
        product?.timestamp ||
        product?.created_at;
      if (source && typeof source === 'object' && typeof source.seconds === 'number') {
        const seconds = source.seconds * 1000;
        const nanoseconds =
          typeof source.nanoseconds === 'number' ? source.nanoseconds / 1e6 : 0;
        return seconds + nanoseconds;
      }
      if (typeof source === 'string') {
        const parsed = Date.parse(source);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
      if (typeof source === 'number') {
        return source;
      }
      return 0;
    },
    scrollLeft() {
      this.$refs.productSlider.scrollBy({ left: -300, behavior: 'smooth' });
    },
    scrollRight() {
      this.$refs.productSlider.scrollBy({ left: 300, behavior: 'smooth' });
    },
    toggleCart() {
      this.cartVisible = !this.cartVisible;
    },
    addToCart(productOrName) {
      const productName = typeof productOrName === 'string' ? productOrName : productOrName?.name;
      if (!productName) {
        return;
      }
      const product = this.products.find(p => p.name === productName);
      if (!product) {
        return;
      }
      const existingItem = this.cartItems.find(item => item.product === productName);
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        this.cartItems.push({ product: productName, quantity: 1 });
      }
      this.saveCartToStorage();
      this.cartVisible = true;
    },
    increaseCartItem(index) {
      if (this.cartItems[index]) {
        this.cartItems[index].quantity += 1;
        this.saveCartToStorage();
      }
    },
    decreaseCartItem(index) {
      const item = this.cartItems[index];
      if (!item) {
        return;
      }
      if (item.quantity > 1) {
        item.quantity -= 1;
      } else {
        this.cartItems.splice(index, 1);
      }
      this.saveCartToStorage();
    },
    removeFromCart(index) {
      this.cartItems.splice(index, 1);
      this.saveCartToStorage();
    },
    getProductPrice(product) {
      if (!product) {
        return 0;
      }
      if (typeof product.price === 'number') {
        return product.price;
      }
      if (typeof product.price === 'string') {
        return parseFloat(product.price.replace(/[^\d.]/g, '')) || 0;
      }
      return 0;
    },
    getCartItemPrice(item) {
      const product = this.products.find(p => p.name === item.product);
      return this.getProductPrice(product);
    },
    checkoutCart() {
      if (!this.cartItems.length) {
        return;
      }
      this.orderItems = this.cartItems.map(item => ({
        product: item.product,
        quantity: item.quantity
      }));
      this.cartVisible = false;
      this.showOrderDialog = true;
      if (!this.shippingOption) {
        this.shippingOption = 'standard';
      }
    },
    loadCartFromStorage() {
      if (typeof window === 'undefined') return;
      try {
        const stored = localStorage.getItem('bioedgeCart');
        if (!stored) {
          return;
        }
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          this.cartItems = parsed
            .filter(item => item && item.product)
            .map(item => ({
              product: item.product,
              quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1
            }));
        }
      } catch (error) {
        console.warn('Unable to load cart from localStorage:', error);
      }
    },
    saveCartToStorage() {
      if (typeof window === 'undefined') return;
      try {
        localStorage.setItem('bioedgeCart', JSON.stringify(this.cartItems));
      } catch (error) {
        console.warn('Unable to save cart to localStorage:', error);
      }
    },
    clearCartStorage() {
      if (typeof window === 'undefined') return;
      try {
        localStorage.removeItem('bioedgeCart');
      } catch (error) {
        console.warn('Unable to clear cart storage:', error);
      }
    },
    recordOrderLocally(orderMeta) {
      if (typeof window === 'undefined') return;
      try {
        const existing = JSON.parse(localStorage.getItem('bioedgeOrders') || '[]');
        const updated = [
          {
            ...orderMeta,
            recordedAt: new Date().toISOString()
          },
          ...existing
        ].slice(0, 50);
        localStorage.setItem('bioedgeOrders', JSON.stringify(updated));
      } catch (error) {
        console.warn('Unable to record order locally:', error);
      }
    },
    cancelOrder() {
      this.showOrderDialog = false;
      this.orderItems = [];
      this.orderSubmitting = false;
    },
    async submitOrder() {
      if (this.orderSubmitting) return;
      
      this.orderSubmitting = true;
      
      try {
        // Validate required fields
        if (!this.orderFirstName || !this.orderLastName || !this.orderEmail || !this.orderCell || !this.orderAddress) {
          alert('Please fill in all required fields');
          return;
        }
        
        if (this.orderItems.length === 0) {
          alert('Please add at least one product to your cart before checking out.');
          return;
        }
        
        // Map products to Firebase format
        const products = this.orderItems.map(item => {
          const product = this.products.find(p => p.name === item.product);
          if (!product) return null;
          
          // Handle price as either number or string
          let price;
          if (typeof product.price === 'string') {
            price = parseFloat(product.price.replace(/[^\d.]/g, ''));
          } else if (typeof product.price === 'number') {
            price = product.price;
          } else {
            price = 0;
          }
          
          return {
            id: product.firebaseId || `product-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: product.name,
            price: price,
            costPrice: product.costPrice || (price * 0.5), // Estimate 50% margin
            quantity: item.quantity,
            courseDuration: product.courseDuration || 12,
            reorderWeeks: product.reorderWeeks || 10,
            courseDescription: product.description || product.courseDescription || `${product.name} treatment program`
          };
        }).filter(Boolean);
        
        // Parse address
        const addressParts = this.orderAddress.split(',').map(part => part.trim());
        const street = addressParts[0] || this.orderAddress;
        const city = addressParts[1] || '';
        const postalCode = addressParts[2] || '';
        
        // Prepare checkout data for Firebase
        const checkoutData = {
          customerInfo: {
            firstName: this.orderFirstName,
            lastName: this.orderLastName,
            email: this.orderEmail,
            phone: this.orderCell
          },
          products: products,
          shippingInfo: {
            address: street,
            city: city,
            postalCode: postalCode,
            method: this.shippingOption,
            service: this.selectedShippingOption?.label || 'Standard Delivery',
            cost: this.shippingCost
          },
          paymentInfo: {
            method: 'bank_transfer',
            status: 'pending' // Will be updated when payment is received
          },
          notes: 'Order placed via website'
        };
        
        // Process order through Firebase
        const result = await processCheckoutOrder(checkoutData);
        
        if (result.success) {
          // Create WhatsApp message with order ID
          const orderDetails = formatOrderedItemsForWhatsApp(this.orderItems);

          const baseURL = 'https://wa.me/27716573969';
          const message = `Hi! I'd like to place an order:

Name: ${this.orderFirstName} ${this.orderLastName}
Email: ${this.orderEmail}
Cell: ${this.orderCell}
Address: ${this.orderAddress}

Ordered Items:
${orderDetails}

Delivery: ${this.selectedShippingOption?.label ?? 'Standard Delivery'} (${this.selectedShippingOption?.description ?? '2-3 days'}) - R${this.shippingCost}
Total: R${this.orderTotal}

Special instructions for courier:`;

          const encodedMessage = encodeURIComponent(message);
          this.recordOrderLocally({
            id: result.orderFirebaseId || `home-${Date.now()}`,
            customer: `${this.orderFirstName} ${this.orderLastName}`.trim(),
            total: this.orderTotal,
            items: this.orderItems.map(item => ({
              name: item.product,
              quantity: item.quantity
            })),
            source: 'home',
            shipping: {
              method: this.shippingOption,
              label: this.selectedShippingOption?.label || 'Standard Delivery',
              cost: this.shippingCost
            }
          });

          saveCheckoutContact({
            firstName: this.orderFirstName,
            lastName: this.orderLastName,
            email: this.orderEmail,
            phone: this.orderCell,
            address: this.orderAddress,
            shippingOption: this.shippingOption
          });

          // Close modal and clear cart; contact fields stay for next order
          this.cancelOrder();
          this.cartItems = [];
          this.clearCartStorage();
          
          // Cross-platform WhatsApp redirect
          this.openWhatsApp(`${baseURL}?text=${encodedMessage}`);
          
        } else {
          throw new Error(result.error || 'Failed to submit order');
        }
        
      } catch (error) {
        console.error('Order submission error:', error);
        alert(`Error submitting order: ${error.message}\n\nPlease try again or contact support.`);
      } finally {
        this.orderSubmitting = false;
      }
    }
,
    openWhatsApp(whatsappUrl) {
      // Cross-platform WhatsApp redirect that works on both iOS and Android
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      try {
        if (isIOS && isSafari) {
          // iOS Safari: Use location.href for better compatibility
          window.location.href = whatsappUrl;
        } else if (isIOS) {
          // iOS Chrome/other browsers: Try window.open first, fallback to location.href
          const newWindow = window.open(whatsappUrl, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
            // If popup was blocked, fallback to location.href
            window.location.href = whatsappUrl;
          }
        } else {
          // Android and other platforms: Use window.open
          const newWindow = window.open(whatsappUrl, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
            // If popup was blocked, fallback to location.href
            window.location.href = whatsappUrl;
          }
        }
      } catch (error) {
        console.warn('WhatsApp redirect failed, using fallback:', error);
        // Final fallback: direct navigation
        window.location.href = whatsappUrl;
      }
    },
    // Test Firebase Connection
    async testFirebase() {
      console.log('Testing Firebase from HomeView...');
      const result = await testFirebaseConnection();
      if (result.success) {
        alert(`Firebase Test Successful!\nProducts: ${result.products.length}\nClients: ${result.clients.length}\nOrders: ${result.orders.length}`);
      } else {
        alert(`Firebase Test Failed: ${result.error}`);
      }
    },
    
    async testCreateTestClient() {
      console.log('Testing client creation from HomeView...');
      const result = await testCreateClient();
      if (result.success) {
        alert(`Test client created successfully! ID: ${result.clientId}`);
      } else {
        alert(`Client creation failed: ${result.error}`);
      }
    },
    
    async seedProductsToFirebase() {
      console.log('Creating all BioEdge products in Firebase...');
      const result = await productService.createAllBioEdgeProducts();
      if (result.success) {
        alert(`Products created successfully! Created: ${result.created} products. Refreshing products...`);
        await this.loadProducts(); // Reload products after seeding
      } else {
        alert(`Product creation failed: ${result.error}`);
      }
    },
    
    formatPrice(price) {
      // Handle different price formats
      if (typeof price === 'string') {
        // Remove R prefix if it exists and convert to number
        const numericPrice = parseFloat(price.replace(/[^\d.]/g, ''));
        return numericPrice.toLocaleString('en-ZA');
      } else if (typeof price === 'number') {
        return price.toLocaleString('en-ZA');
      } else {
        return '0';
      }
    },
    
    async loadProducts() {
      try {
        console.log('Loading products from Firebase...');
        const firebaseProducts = await productService.getAllProducts();
        
        // Transform Firebase products to match the expected format
        this.products = firebaseProducts.map(product => ({
          ...product,
          // Ensure price is always a number
          price: typeof product.price === 'number' ? product.price : 
                 typeof product.price === 'string' ? parseFloat(product.price.replace(/[^\d.]/g, '')) || 0 : 0,
          firebaseId: product.id // Store Firebase ID for reference
        }));
        
        console.log(`Loaded ${this.products.length} products from Firebase`);
      } catch (error) {
        console.error('Error loading products from Firebase:', error);
        // Fallback to empty array if Firebase fails
        this.products = [];
      }
    }
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize)
    AOS.init({ duration: 1000, once: false });
    window.addEventListener('load', () => {
      setTimeout(() => {
        AOS.refreshHard();
      }, 100);
    });
    
    this.loadCartFromStorage();
    applyCheckoutContactToData(this, loadCheckoutContact());
    // Load products from Firebase
    await this.loadProducts();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap');

body {
  background-color: white;
}

.banner-shell {
  width: 100%;
  max-width: none;
  padding-left: clamp(1rem, 4vw, 4.5rem);
  padding-right: clamp(1rem, 3vw, 3rem);
  margin: 0;
}

.banner-content {
  padding: clamp(0rem, 2vw, 2rem) 0;
  max-width: 34rem;
}

.navbar {
  display: flex;
  align-items: center;
  gap: clamp(1.5rem, 4vw, 3rem);
  font-family: 'Montserrat', sans-serif;
}

.navbar-links {
  display: flex;
  align-items: center;
  gap: clamp(1.25rem, 3vw, 2.6rem);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.navbar-links a {
  position: relative;
  padding-bottom: 0.25rem;
  color: inherit;
  transition: color 0.25s ease;
}

.navbar-links a::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: rgba(255, 255, 255, 0.85);
  transform: scaleX(0);
  transform-origin: center;
  opacity: 0;
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.navbar-links a:hover,
.navbar-links a:focus {
  color: #52cd36;
}

.navbar-links a:hover::after,
.navbar-links a:focus::after {
  opacity: 1;
  transform: scaleX(1);
  background: #52cd36;
}

.banner-header {
  background: linear-gradient(
    180deg,
    rgba(6, 18, 3, 0.92) 0%,
    rgba(6, 18, 3, 0.75) 55%,
    rgba(6, 18, 3, 0) 100%
  );
}

.navbar-cta {
  font-size: 0.9rem;
  letter-spacing: 0.05em;
  text-transform: none;
}

.banner-eyebrow {
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

@media (min-width: 640px) {
  .banner-eyebrow {
    font-size: 0.875rem;
    letter-spacing: 0.4rem;
  }
}

.banner-title {
  display: flex;
  flex-direction: column;
  gap: 0.1em;
  margin-top: 1.5rem;
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(3rem, 6vw, 5.2rem);
  font-weight: 800;
  line-height: 0.95;
  text-transform: uppercase;
  color: #ffffff;
  text-shadow: 0 12px 45px rgba(0, 0, 0, 0.55);
}

.banner-subtext {
  margin-top: 2rem;
  max-width: 32rem;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  line-height: 1.85;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.banner-actions a {
  font-family: 'Montserrat', sans-serif;
}

.services-eyebrow {
  display: block;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(17, 24, 39, 0.6);
  margin-bottom: 0.75rem;
}

@media (min-width: 640px) {
  .services-eyebrow {
    font-size: 0.875rem;
    letter-spacing: 0.4rem;
  }
}

.services-heading {
  margin: 0 auto;
  padding-top: 0.5rem;
  font-family: 'Montserrat', sans-serif;
  font-weight: 800;
  line-height: 1.25;
  color: #52cd36;
  font-size: clamp(1.25rem, 2.6vw, 2rem);
  text-rendering: optimizeLegibility;
}

.products-subheading {
  margin-top: 1.25rem;
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.7;
}

#services .services-heading {
  font-size: clamp(1.1rem, 1.9vw, 1.6rem);
  line-height: 1.2;
}

.services-grid {
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 1.75rem;
}

@media (min-width: 768px) {
  .services-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .services-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.service-item {
  text-align: center;
}

.service-icon {
  width: 7.5rem;
  height: 7.5rem;
  margin: 0 auto 1.25rem auto;
  border-radius: 9999px;
  background: linear-gradient(135deg, #52cd36, #46b42f);
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 14px 28px rgba(82, 205, 54, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}

.service-icon svg {
  width: 2.25rem;
  height: 2.25rem;
}

.service-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}

.service-desc {
  margin-top: 0.5rem;
  color: #6b7280;
  font-size: 0.925rem;
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.7rem 2.2rem;
  border-radius: 9999px;
  font-size: clamp(0.875rem, 1vw, 1rem);
  font-weight: 600;
  letter-spacing: 0.05em;
  min-width: 9.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
}

.banner-primary-btn {
  background: linear-gradient(135deg, #52cd36, #46b42f);
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 20px 40px rgba(82, 205, 54, 0.32);
}

.banner-primary-btn:visited,
.banner-primary-btn:hover,
.banner-primary-btn:focus {
  color: #ffffff !important;
}

.banner-primary-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 26px 52px rgba(82, 205, 54, 0.4);
}

.banner-primary-btn:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(82, 205, 54, 0.28);
}

.banner-secondary-btn {
  background-color: rgba(6, 18, 3, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #ffffff !important;
  box-shadow: 0 18px 36px rgba(6, 18, 3, 0.35);
  backdrop-filter: blur(8px);
}

.banner-secondary-btn:visited,
.banner-secondary-btn:hover,
.banner-secondary-btn:focus {
  color: #ffffff !important;
}

.banner-secondary-btn:hover {
  transform: translateY(-3px);
  background-color: rgba(6, 18, 3, 0.45);
  border-color: rgba(255, 255, 255, 0.55);
  box-shadow: 0 26px 52px rgba(6, 18, 3, 0.42);
}

.banner-secondary-btn:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(82, 205, 54, 0.28);
}

.view-all-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #ffffff;
  padding: 0.75rem 2.5rem;
  border-radius: 9999px;
  background: linear-gradient(135deg, #52cd36, #3da026);
  box-shadow: 0 20px 40px rgba(82, 205, 54, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.view-all-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 26px 52px rgba(82, 205, 54, 0.35);
}

.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.testimonials-section {
  background: #ffffff;
  padding: clamp(3rem, 6vw, 5rem) 0;
}

.testimonials-header {
  max-width: 42rem;
  margin: 0 auto clamp(2.5rem, 6vw, 3.5rem);
}

.testimonials-subheading {
  margin-top: 1.25rem;
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.7;
}

.testimonials-grid {
  display: grid;
  gap: clamp(1.5rem, 4vw, 2.25rem);
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.testimonials-card {
  background: #ffffff;
  border-radius: 24px;
  padding: clamp(1.5rem, 4vw, 2rem);
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 24px 48px rgba(18, 48, 22, 0.12);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.testimonials-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 32px 64px rgba(18, 48, 22, 0.16);
}

.testimonials-card__header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.testimonials-card__avatar {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 9999px;
  object-fit: cover;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
}

.testimonials-card__name {
  font-weight: 700;
  font-size: 1.05rem;
  color: #111827;
}

.testimonials-card__location {
  font-size: 0.85rem;
  color: #6b7280;
}

.testimonials-card__quote {
  font-size: 0.95rem;
  line-height: 1.7;
  color: #374151;
}

.custom-banner-enter-active {
  animation: bannerCustom 1.5s ease-out forwards;
}

.custom-banner-leave-active {
  animation: bannerCustom 1.5s ease-out reverse forwards;
}

@keyframes bannerCustom {
  0% {
    transform: translateY(-50px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

button,
a[role='button'] {
  transition: all 0.3s ease;
}

/* Line clamp utility */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}

/* Product card hover effects */
/* Smooth, touch-friendly modal scrolling */
.modal-scroll {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.home-products-slider {
  scroll-padding-left: 1rem;
}

.home-products-track {
  display: flex;
  gap: clamp(1.25rem, 3vw, 2rem);
  min-width: max-content;
  padding-right: 1rem;
  justify-content: center;
}

.home-products-card {
  flex: 0 0 auto;
  width: min(320px, 80vw);
}

.order-summary {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.order-summary__item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  color: #0f1d12;
}

.cart-slide-enter-active .cart-drawer,
.cart-slide-leave-active .cart-drawer {
  transition: transform 0.25s ease;
}

.cart-slide-enter-from .cart-drawer,
.cart-slide-leave-to .cart-drawer {
  transform: translateX(100%);
}
</style>










